/* Threnvik Qalmoth palette. Chrome tokens (surfaces / text / borders / overlays)
   are `var(--…)` references into src/styles/tokens.css, so a single
   `data-theme="dark"` flip re-themes every component that reads `C`. Chart
   series, brand accents and map data tones stay as literal hex — they are
   intentional data-viz colours and must NOT change between themes. */
export const C = {
  /* Surfaces — themeable */
  bg: "var(--color-bg-primary)",
  bgSoft: "var(--color-bg-secondary)",
  surface: "var(--color-surface)",
  /* Always-opaque surface for overlays that must stay legible even where
     `surface` is translucent glass (the observatory shell) — e.g. chart tooltips. */
  tooltipBg: "var(--color-tooltip-bg)",
  panelAlt: "var(--color-surface-alt)",
  border: "var(--color-border)",
  borderStrong: "var(--color-border-strong)",

  /* Text — themeable */
  textHi: "var(--color-text-primary)",
  textMd: "var(--color-text-secondary)",
  textLo: "var(--color-text-muted)",
  onAccent: "var(--color-text-on-accent)" /* text on navy/accent fills */,

  /* Accent — themeable */
  accent: "var(--color-accent)",
  accentHover: "var(--color-accent-hover)",

  /* Strong fill — navy headers / active pills / table headers. Stays dark in
     both themes; pair with onFillStrong for the text so it never inverts. */
  fillStrong: "var(--color-fill-strong)",
  onFillStrong: "var(--color-on-fill-strong)",

  /* Series — chart palette. Saturated brand hues are themed (light value in
     tokens.css, lifted on dark for WCAG). `spend` (amber data) aliases gold-fill
     so spend and gold-fill are ONE token — bright #FFBF00 on dark (byte-identical
     to the old literal), legible burnt-gold on the light warm surface. Bright
     gold-on-a-permanent-dark-island stays `C.gold`. */
  arrivals: "var(--color-series-arrivals)",
  spend: "var(--color-gold-fill)",
  spv: "var(--color-series-purple)",
  emerging: "var(--color-series-emerging)",
  crisis: "var(--color-series-crisis)",

  /* Standard list/magnitude-bar fill — one teal-sage everywhere, with a
     tone-matched clay for declining/negative bars. Themed: light value in
     tokens.css, lifted on dark so the bars don't go dull. */
  bar: "var(--color-series-bar)",
  barNeg: "var(--color-series-barneg)",

  /* Soft chart fill / hover bg / gridlines — themeable overlays */
  grid: "var(--color-overlay-grid)",
  mute: "var(--color-overlay)",
  muteStrong: "var(--color-overlay-strong)",

  /* Brand accents (NOT themed — used in charts + brand chrome) */
  gold: "#FFBF00",
  goldText: "var(--color-amber-text)" /* gold-toned but legible as TEXT (dark amber on light, gold on dark) */,
  goldSerif: "#E9C977" /* landing-hub serif accent gold — italic Empira emphasis on dark shells */,
  goldLight: "var(--color-highlight-soft)",
  /* Per-workspace selection-row highlights, keyed to each workspace's DATA hue
     (not its chrome accent). goldLight above is the shared default (correct for
     Prioritization, whose data hue is gold); DS + Intelligence sections override
     --color-highlight-soft with these via IntelShell's `hl` prop so a selected
     ranked row tints in the workspace's own data colour, never the DS-shell
     yellow. Fixed literals (like gold/blue) — the data ramps aren't themed. */
  hlAzure: "rgba(130,186,255,0.16)" /* Demand Signals — soft azure */,
  hlTeal: "rgba(38,197,173,0.15)" /* Intelligence — soft teal */,
  hlMagenta: "rgba(178,78,148,0.17)" /* Segmentation — soft magenta */,
  cyan: "#CEECFF",
  blue: "#82BAFF",
  /* Editorial italic accent — theme-aware: a darker legible blue on light, the
     bright data-blue on dark. Chrome accent for section-title emphasis words,
     NOT a data-viz colour (unlike `blue` above, which stays fixed in charts). */
  accentItalic: "var(--color-accent-italic)",
  /* Frosted raised-card fill — theme-aware: solid white on light (lifts off the
     tinted ground so the card doesn't dissolve), translucent-white glass on dark. */
  cardFrost: "var(--card-frost)",
  purple: "var(--color-series-purple)",
  coral:
    "#FF8A5C" /* Demand Signals workspace accent — warm coral/orange, same lightness family as blue/green/purple so it reads as "live/pulse" without clashing with amber (GTM) */,

  /* ── Semantic palette additions (Wave 1) — the consolidated tokens every
     component routes through, so light draws from ONE set and dark stays 1:1.
     Workspace accent-inks: the darkened per-workspace hue on light, raw on dark
     (green=seg-ink, coral=signals-ink, blue=accentItalic already exist). */
  prioInk: "var(--prio-ink)",
  productInk: "var(--product-ink)",
  gtmInk: "var(--gtm-ink)",
  /* Gold as a data FILL/bar on a themed surface (legible dark-gold on light, bright
     gold on dark). Gold TEXT = goldText; gold on a permanent-dark surface = raw gold. */
  goldFill: "var(--color-gold-fill)",
  /* The one categorical chart set (quadrant / scatter) — shared, never per-file copies. */
  catAzure: "var(--cat-azure)",
  catGold: "var(--cat-gold)",
  catRose: "var(--cat-rose)",
  catViolet: "var(--cat-violet)",

  /* Map tones — landWithData/landHover are data-viz (NOT themed); landNoData
     is map chrome (empty land) so it flips with the theme. */
  landWithData: "#CEECFF",
  landNoData: "var(--color-map-land-empty)",
  landHover: "#82BAFF",

  /* Map chrome — ocean backdrop + no-data land. Themeable so choropleths sit
     on the page in both themes (data ramps stay literal, in component code). */
  mapOcean: "var(--color-map-ocean)",
  mapNoData: "var(--color-map-no-data)",
} as const;

/* Darken a bright workspace accent (WORKSPACE_ACCENT) to a legible ink on a LIGHT
   surface, keeping its hue by mixing toward navy (the same ~45% navy the DS shell's
   --ds-accent-ink uses). On DARK it returns the raw accent unchanged — byte-identical —
   so the signature stays bright on the dark shell. Callers pass whether the active
   theme is light (from useTheme). Used for section-title accent words + numerals. */
export const accentInk = (hex: string, light: boolean): string =>
  light ? `color-mix(in oklab, ${hex}, #000f47 45%)` : hex;

/* Categorical-palette light twins — a 1:1 dark→light map for Track A's hand-rolled
   CATEGORY palettes (extraction-schema slabs + data-provenance in methodology.tsx,
   the purpose split in saudi-intel). Those hues are theme-agnostic IDENTITY colors
   picked to glow on the dark shell; several are neon (#FFD93D, #21C77B, #FFA552 …)
   and vanish as TEXT on the light card (measured 1.3–3.0:1 on #f7f9fc). Each dark
   hue keeps its OWN distinct light counterpart here — same hue, darkened to ≥4.5:1
   — so NO category is merged or dropped: light carries exactly as many colors as
   dark. Unlike accentInk (a runtime navy-mix that drifts yellow→olive and can't
   guarantee contrast), these are hand-tuned + contrast-verified. On DARK, catInk
   returns the hex unchanged → byte-identical. Colors already legible on light
   (engine.ts's deep segment tones at 4.8–11:1) are intentionally ABSENT, so catInk
   passes them straight through. */
const CAT_LIGHT: Record<string, string> = {
  "#5B8DFF": "#2C5FD0", // blue
  "#21C77B": "#0F7E52", // green
  "#FFD93D": "#857000", // neon yellow → dark gold
  "#FFA552": "#A85818", // amber → burnt amber
  "#A89BFF": "#6A5AD6", // lavender → deep violet
  "#FF6B9D": "#CC3A6C", // pink → deep rose
  "#8892B0": "#55607E", // slate
  "#1FA37A": "#0E7A5A", // purpose · religious teal
  "#F5B54A": "#8F6410", // purpose · leisure sand
  "#B794F4": "#7A4FC0", // purpose · VFR purple
  "#5B8DEF": "#2C5FD0", // purpose · business blue
  "#94A3B8": "#55607E", // purpose · other slate
  // Visa ease ordinal ramp (VISA_CATS) — the cool→warm progression is kept, each
  // step darkened so it reads on light land as it does on the navy basemap.
  "#4F9E90": "#2E7266", // visa · no-visa teal (easiest)
  "#C2A25B": "#836823", // visa · on-arrival ochre
  "#C17E55": "#9A4E28", // visa · e-visa terracotta
  "#AE6274": "#8A4256", // visa · consular rose (hardest)
  // Product Matching (gtm-product MixBars) — the one data-viz palette still drawn
  // raw on the light card. The cyan RAMP's deep two (#0F5F6E/#1596AC) already read
  // on white → pass-through (absent here); only the bright/pale top steps needed
  // darkening so the pale-cyan segment + legend swatch stop washing out. The warm/
  // green/purple/mint keys reuse the SAME twin families already defined above (gold
  // sand, ds-shell green/danger/amber, VFR purple) so the section stays in-system.
  "#26CFE6": "#2BB2C8", // product · bright cyan → medium cyan
  "#7FE6F2": "#59CBDD", // product · pale cyan (was washed out) → light-medium cyan
  "#16C98D": "#0D8A62", // product · green (ds-shell --ds-p-green family)
  "#E9C46A": "#8F6410", // product · gold (leisure-sand family)
  "#FF6B6B": "#C8272D", // product · danger red (ds-shell --ds-p-danger family)
  "#FFB454": "#A85818", // product · warn amber (burnt-amber family)
  "#B79CFF": "#7A4FC0", // product · purple (VFR-purple family)
  "#7FD7B2": "#4AA883", // product · mint (STAR 4-star) → deeper mint, distinct from green
};
export const catInk = (hex: string, light: boolean): string => (light ? (CAT_LIGHT[hex.toUpperCase()] ?? hex) : hex);

/* Typography — exact match for dashboard's --font-serif / --font-sans */
export const FS = "'Empira', 'Noto Serif', 'Georgia', serif";
export const FU = "'Noto Sans', sans-serif";
/* Mono — instrument labels / data stamps only (design-system v2 §A2). Max one
   mono device per screen region. Mirrors the landing hub's local FM (hub:25). */
export const FM = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

/* ── Navigation "selected" treatment — hue-less, uniform across ALL workspaces ─
   The active item in NAVIGATION (sidebar section rows, collapsed rail numbers,
   the Global/Saudi scope toggle, and the top segmented rail's active chip) is
   marked with a neutral grey, NOT the workspace accent — so "which page/tab is
   selected" reads identically in every workspace (Intelligence, Demand Signals,
   Segmentation, Prioritization, Product Matching, Go to Market). Built from
   --color-text-primary so it holds on the dark shell (and would invert on light).
   This is NAV CHROME ONLY: section CONTENT (kickers, italic headings, card
   numbers, chart hues) keeps its per-workspace accent. */
export const NAV_SEL_FILL = "color-mix(in srgb, var(--color-text-primary) 14%, transparent)";
export const NAV_SEL_EDGE = "color-mix(in srgb, var(--color-text-primary) 82%, transparent)";

/* ── Type scale (Plan 16) ──────────────────────────────────────────────────
   The ONLY sanctioned font sizes. Collapses 26 ad-hoc values to 9 steps so
   hierarchy lines up everywhere. Hierarchy is carried by size + the three text
   tiers (textHi/textMd/textLo) — NOT by heavy weights. Values are unitless
   numbers, used directly as `fontSize: T.body`. */
// Low tiers lifted for legibility (client feedback 2026-08: small grey text read
// as unreadable). cap 12→13, micro 11→12, tag 10→11 — the floor for meaningful
// copy is now 12px; 13 is the shared body/caption size (captions differ from body
// by COLOUR, not size). stat 28→30 uses the spare headroom on KPI cards. The
// deliberate small exceptions (tab/step-rail auto-shrink 11px, mono eyebrows
// 10.5px, the 9px dense chart axes) live outside this scale and are unchanged.
export const T = {
  display: 40, // hero / present-mode title (wrap in clamp() where responsive)
  stat: 30, //    the single big-number size for KPI / stat values
  title: 22, //   section H2
  head: 18, //    sub-heads, panel titles
  lead: 15, //    lead paragraph, stat labels, hub names
  body: 13, //    DEFAULT — body, table cells, controls, source lines
  cap: 13, //     secondary captions (== body size; distinguished by colour)
  micro: 12, //   eyebrows, axis ticks, table-header labels
  tag: 11, //     tags / badges (9 reserved for dense chart axes only, via TYPE.micro)
} as const;

/* ── Weight ladder (Plan 16) ───────────────────────────────────────────────
   Lighter by default. 700/800/900 are retired from app chrome: hierarchy comes
   from size + colour, not boldness.
     400 body · 500 subtitles / active nav / lead · 600 titles / headers / the
     single emphasis per view. */
export const W = {
  /* Empira ships ONE face: 300 normal. Any heavier request on FS is
     browser-synthesized faux bold — so serif display text is ALWAYS W.light.
     Serif hierarchy comes from size + colour + italic accents, never weight. */
  light: 300,
  body: 400,
  medium: 500,
  semibold: 600,
} as const;

/* ── Dark-shell ink (landing hub / observatory surfaces — NOT themed) ──────
   The landing page's exact text palette (landing-hub.tsx is the reference
   design). Use these on the dark navy shells instead of ad-hoc greys. */
export const INK = {
  hi: "#E8EEFB", //     primary text
  md: "#8993AC", //     secondary text
  faint: "#5C6680", //  tile labels / faint mono
  eyebrow: "#7C87A4", //mono eyebrows / kickers
  pos: "#16C98D", //    positive stat text on dark
  neg: "#FF8F8F", //    negative stat text on dark
} as const;

/* ── Light-shell ink (the light twin of INK) ───────────────────────────────
   Same six roles, re-derived to clear WCAG AA on a white / near-white surface.
   Used where a shell or hero surface flips light (hero-row, the light hub,
   any INK-based surface) — pick INK vs INK_LIGHT off the active theme. Navy
   family so it reads as "the same ink, darker", not a different palette. */
export const INK_LIGHT = {
  hi: "#000f47", //     primary text · 17:1 (TQ navy, == --color-text-primary)
  md: "#55617f", //     secondary text · 6.2:1
  faint: "#767f95", //  tile labels / faint mono · 4.5:1
  eyebrow: "#64708c", //mono eyebrows / kickers · ~4.9:1
  pos: "#15803d", //    positive stat text on white · 4.9:1
  neg: "#c8272d", //    negative stat text on white · 5.6:1
} as const;

/* ── Unified type ramp (landing parity) ────────────────────────────────────
   One style per semantic role, everywhere. DNA (from the landing hub):
   Empira Light for anything expressive — 300 only, italic for accents, sized
   not bolded; JetBrains Mono for every label-class element (eyebrows, chips,
   CTAs, table/axis labels); Noto Sans (FU) only for paragraph body copy.
   Spread into style props: `style={{ ...TYPE.title, color: INK.hi }}`. */
export const TYPE = {
  /* Empira display voice */
  hero: { fontFamily: FS, fontWeight: 300, fontSize: "clamp(34px, 4vw, 49px)", lineHeight: 1.05 },
  title: { fontFamily: FS, fontWeight: 300, fontSize: 26, lineHeight: 1.15 }, // step/section titles
  cardTitle: { fontFamily: FS, fontWeight: 300, fontSize: 19, lineHeight: 1.15 },
  statXL: { fontFamily: FS, fontWeight: 300, fontStyle: "italic", fontSize: 44, lineHeight: 1 },
  stat: { fontFamily: FS, fontWeight: 300, fontStyle: "italic", fontSize: 30, lineHeight: 1 },
  /* Mono label voice — two sanctioned trackings only (0.28em eyebrow / 0.16em label) */
  eyebrow: { fontFamily: FM, fontWeight: 400, fontSize: 10.5, letterSpacing: "0.28em" },
  label: { fontFamily: FM, fontWeight: 400, fontSize: 10.5, letterSpacing: "0.16em" },
  micro: { fontFamily: FM, fontWeight: 400, fontSize: 9, letterSpacing: "0.13em" },
} as const;

/* Layout — width of the left sidebar (section tree). Plan 17 retired the
   far-left peer rail (RAIL_W), so the sidebar now anchors flush to the screen
   edge. Kept here so dashboard.tsx and section-tree.tsx stay in sync. */
export const TREE_W = 248;
export const TREE_W_COLLAPSED = 56;

/* The one control height. Every interactive control that can sit beside another
   one — dropdown triggers, filter chips, in-band CTAs, Reset — is exactly this
   tall, so a row of them shares a baseline instead of stair-stepping.
   36 is not a new number: it is what the sticky band's row 1 has always
   reserved (`min-height: 36px` in .intel-topbar-row / .gtd-slim-row /
   .seg-topbar-row) and what GTM's in-band dropdowns already force. It was a
   copy-pasted CSS literal in four files with no token behind it, which is how
   the Segmentation section drifted to seven different control heights
   (43 picker / 38 filter chip / 38 reset / 36 band / 34 rail step / 24
   methodology). Import this instead of typing 36. */
export const CONTROL_H = 36;
